import { Loader } from '@10xLabs/react-web/components/Loader'
import { NetworkStatus, useMutation, useQuery } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useEffect, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from 'components/v2/Button'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import BusinessCustomerList from 'features/BusinessCustomer/components/BusinessCustomerList/List'
import BusinessCustomerListPagination from 'features/BusinessCustomer/components/BusinessCustomerList/Pagination'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { useLogin } from 'features/Login/hooks'
import { canRead, canWrite } from 'features/Users/utils'
import {
  GET_BUSINESS_CUSTOMER_SUGGESTIONS,
  GET_BUSINESS_CUSTOMERS_LIST
} from 'features/BusinessCustomer/queries-legacy'
import { BusinessCustomerCSVModal } from 'features/BusinessCustomer/components/CustomerCSVModal'
import { IBusinessCustomerCSVFormValues, IBusinessCustomersFiltersForm } from 'features/BusinessCustomer/types'
import {
  BusinessCustomerFormUtils,
  mapFiltersToQueryVariables,
  mapFiltersToSearchQueryString,
  mapSearchToFiltersForm
} from 'features/BusinessCustomer/utils'
import { CREATE_BUSINESS_CUSTOMERS_FROM_CSV } from 'features/BusinessCustomer/mutations-legacy'
import styled from 'styled-components'
import { BusinessCustomersFilters } from 'features/BusinessCustomer/components/BusinessCustomerList/Filters'
import { filterEmptyValue } from 'utils/object'
import { GTBusinessCustomersResponse, GTBusinessCustomerSuggestionsResponse } from 'gql/types.generated'
import { getQueries } from 'utils/route'
import { useSetLastUrlWithFilters } from 'hooks/urls'
import { useBusinessClientAutocomplete } from 'features/BusinessClients/hooks'
import { useBusinessSiteAutocomplete } from 'features/BusinessSites/hooks'

const messages = defineMessages({
  helmet: {
    id: 'businessCustomer.helmet',
    defaultMessage: 'Business Customers | Nexbus Admin'
  }
})

const ButtonsContainer = styled.div`
  display: flex;
  gap: 16px;
`
const BUSINESS_CUSTOMER_PAGE_LIMIT = 100

const delayTimeForCustomerCreation = async (): Promise<void> =>
  await new Promise((resolve) => setTimeout(resolve, 5000))

const BusinessCustomerView = (): ReactElement => {
  let content: ReactElement
  const intl = useIntl()
  const login = useLogin()
  const navigate = useNavigate()
  useSetLastUrlWithFilters()
  const [searchParams] = useSearchParams()
  const [openDrawer, setOpenDrawer] = useState(false)
  const [loadingCreatingCustomersFromCsv, setLoadingCreatingCustomersFromCsv] = useState(false)
  const pageNb = parseInt(searchParams.get('page') ?? '1')
  const hasPermissions = canRead(FEATURES.BUSINESS_CUSTOMER, login.accessToken.policies)
  const hasWritePermissions = canWrite(FEATURES.BUSINESS_CUSTOMER, login.accessToken.policies)
  const searchQueries = getQueries(location.search)
  const businessCustomerIdsList = searchQueries.ids ? searchQueries.ids.split(',') : []
  const businessClientAutocomplete = useBusinessClientAutocomplete(searchQueries.clientId, {
    skip: !searchQueries.clientId
  })
  const businessSiteAutocomplete = useBusinessSiteAutocomplete(searchQueries.siteId, {
    skip: !searchQueries.siteId
  })
  const { data: businessCustomersData } = useQuery<{
    businessCustomerSuggestions: GTBusinessCustomerSuggestionsResponse
  }>(GET_BUSINESS_CUSTOMER_SUGGESTIONS, {
    fetchPolicy: 'network-only',
    variables: {
      input: {
        ids: businessCustomerIdsList
      }
    },
    skip: isEmpty(businessCustomerIdsList)
  })

  const filters = mapSearchToFiltersForm(searchQueries, {
    businessCustomers: businessCustomersData?.businessCustomerSuggestions.items || [],
    businessClient: businessClientAutocomplete,
    businessSite: businessSiteAutocomplete
  })

  const { loading, error, data, refetch, networkStatus } = useQuery<{
    businessCustomers: GTBusinessCustomersResponse
  }>(GET_BUSINESS_CUSTOMERS_LIST, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !hasPermissions,
    variables: {
      input: mapFiltersToQueryVariables(filters),
      pagination: {
        size: BUSINESS_CUSTOMER_PAGE_LIMIT,
        page: pageNb
      }
    }
  })

  const [createBusinessCustomerFromCsv, { error: createCustomerFromCsvError }] = useMutation(
    CREATE_BUSINESS_CUSTOMERS_FROM_CSV,
    {
      onError: (): void => {
        // Do nothing
      }
    }
  )

  const handleCSVSubmit = async (formValues: IBusinessCustomerCSVFormValues): Promise<void> => {
    setLoadingCreatingCustomersFromCsv(true)
    const { data } = await createBusinessCustomerFromCsv({
      variables: {
        input: BusinessCustomerFormUtils.mapCSVFormToMutation(formValues)
      }
    })
    if (data?.createBusinessCustomersFromCSV) {
      await delayTimeForCustomerCreation()
      refetch()
      setLoadingCreatingCustomersFromCsv(false)
      setOpenDrawer(false)
    } else {
      setLoadingCreatingCustomersFromCsv(false)
    }
  }

  const handleFiltersSubmit = (values: IBusinessCustomersFiltersForm): void => {
    navigate(`/business-customers?${mapFiltersToSearchQueryString(values)}`)
  }

  useEffect((): void => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  const isInitialBusinessCustomersPageEmpty =
    isEmpty(data?.businessCustomers.items) && pageNb === 1 && isEmpty(filterEmptyValue(filters))

  const isEmptyFilteredData = isEmpty(data?.businessCustomers.items) && !isEmpty(filterEmptyValue(filters))

  if (!hasPermissions) {
    return <ForbiddenView feature={FEATURES.BUSINESS_CUSTOMER} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="businessCustomer.error.description"
            defaultMessage="There was an error while loading your data"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            onClick={(): void => {
              refetch()
            }}
            kind="primary"
          >
            <FormattedMessage id="businessCustomer.error.reload" defaultMessage="Reload" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isInitialBusinessCustomersPageEmpty) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="businessCustomer.noSearchResults.message"
            defaultMessage="You don't have any Business Customers yet"
          />
        </EmptyState.Description>
        {hasWritePermissions && (
          <EmptyState.Action>
            <Button as={NavLink} to="/business-customers/new" kind="primary">
              <FormattedMessage id="businessCustomer.empty.action" defaultMessage="Add your first Customer" />
            </Button>
          </EmptyState.Action>
        )}
      </EmptyState.Container>
    )
  } else if (isEmptyFilteredData) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="businessCustomer.empty.message"
            defaultMessage="There were no Business Customers found according to your filters"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/business-customers" kind="primary">
            <FormattedMessage id="businessCustomer.empty.filters.action" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <BusinessCustomerList items={data?.businessCustomers.items ?? []} />
        <BusinessCustomerListPagination
          page={pageNb}
          totalItems={data?.businessCustomers.total ?? 0}
          currentItemsCount={data?.businessCustomers.items.length ?? 0}
          limit={BUSINESS_CUSTOMER_PAGE_LIMIT}
          filters={filters}
        />
      </>
    )
  }

  return (
    <>
      <Content>
        <ContentHeader>
          <Heading.H3>
            <FormattedMessage id="businessCustomer.header.title" defaultMessage="Business Customers List" />
          </Heading.H3>
          {hasWritePermissions && (
            <ButtonsContainer>
              <Button kind="primary" type="button" onClick={(): void => setOpenDrawer(true)}>
                <FormattedMessage id="businessCustomer.uploadCSV" defaultMessage="Upload CSV" />
              </Button>
              <Button as={NavLink} to="/business-customers/new" kind="primary">
                <FormattedMessage id="businessCustomer.header.add" defaultMessage="+ Add Customer" />
              </Button>
            </ButtonsContainer>
          )}
        </ContentHeader>
        <ContentBody>
          <BusinessCustomersFilters filters={filters} onSubmit={handleFiltersSubmit} />
          <section>{content}</section>
        </ContentBody>
      </Content>
      {openDrawer && (
        <BusinessCustomerCSVModal
          error={createCustomerFromCsvError}
          loading={loadingCreatingCustomersFromCsv}
          setIsOpen={setOpenDrawer}
          onSubmit={handleCSVSubmit}
        />
      )}
    </>
  )
}

export default withLastUrl(BusinessCustomerView)
